import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';

interface KeyRequest {
  id: number;
  keyIds: number[];
  reason: string;
  requestedBy: string;
  requestedDate: string;
  approvalStatus: string;
  approvedBy: string | null;
}

interface KeyRequestTableProps {
  data: KeyRequest[];
  onActionSuccess?: () => void; // Callback to refresh data after action
}

const KeyRequestTable: React.FC<KeyRequestTableProps> = ({ data, onActionSuccess }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<KeyRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleViewClick = async (id: number) => {
    setIsLoading(true);
    try {
      const res = await apiDefinitions.getKeyRequestById(id);
      if (res.data.status === 'S1000') {
        setSelectedRequest(res.data.data);
        setShowDetailsModal(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data.statusDescription || 'Failed to fetch request details.',
          confirmButtonText: 'OK',
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.statusDescription || 'Failed to fetch request details.',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (id: number, approvalStatus: 'APPROVED' | 'REJECTED') => {
    setIsLoading(true);
    try {
      const payload = { approvalStatus };
      const res = await apiDefinitions.approveKeyRequest(id, payload); // Pass id and payload correctly
      if (res.data.status === 'S1000') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Key request ${approvalStatus.toLowerCase()} successfully.`,
          confirmButtonText: 'OK',
        });
        if (onActionSuccess) onActionSuccess(); // Trigger refresh of parent component data
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data.statusDescription || `Failed to ${approvalStatus.toLowerCase()} key request.`,
          confirmButtonText: 'OK',
        });
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? 'Key request endpoint not found. Please check the server configuration.'
          : err?.response?.data?.statusDescription || `Failed to ${approvalStatus.toLowerCase()} key request.`;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonText: 'OK',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>Key IDs</th>
              <th>Reason</th>
              <th>Requested By</th>
              <th>Requested Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((request) => (
              <tr key={request.id}>
                <td>{request.id}</td>
                <td>{request.keyIds.join(', ')}</td>
                <td>{request.reason}</td>
                <td>{request.requestedBy}</td>
                <td>{new Date(request.requestedDate).toLocaleString()}</td>
                <td>
                  <span className="badge bg-warning">{request.approvalStatus}</span>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-info me-1"
                    onClick={() => handleViewClick(request.id)}
                    disabled={isLoading}
                    title="View Details"
                  >
                    <i className="ti ti-eye"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-success me-1"
                    onClick={() => handleAction(request.id, 'APPROVED')}
                    disabled={isLoading || request.approvalStatus !== 'PENDING'}
                    title="Approve"
                  >
                    <i className="ti ti-check"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleAction(request.id, 'REJECTED')}
                    disabled={isLoading || request.approvalStatus !== 'PENDING'}
                    title="Reject"
                  >
                    <i className="ti ti-x"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Key Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest ? (
            <div>
              <p><strong>ID:</strong> {selectedRequest.id}</p>
              <p><strong>Key IDs:</strong> {selectedRequest.keyIds.join(', ')}</p>
              <p><strong>Reason:</strong> {selectedRequest.reason}</p>
              <p><strong>Requested By:</strong> {selectedRequest.requestedBy}</p>
              <p><strong>Requested Date:</strong> {new Date(selectedRequest.requestedDate).toLocaleString()}</p>
              <p><strong>Approval Status:</strong> {selectedRequest.approvalStatus}</p>
              <p><strong>Approved By:</strong> {selectedRequest.approvedBy || 'N/A'}</p>
            </div>
          ) : (
            <p>Loading details...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseDetailsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default KeyRequestTable;